import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../modules/auth';


export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  const url = environment.apiUrl;
  
  // Check if user is on reset-password page (with query params like ?userId=...&code=...)
  // Check both pathname AND full href to catch the page even during initial load
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const currentFullUrl = typeof window !== 'undefined' ? window.location.href : '';
  const isOnResetPasswordPage = currentPath.includes('/auth/reset-password') || 
                                 currentPath.includes('/auth/reset-pass') ||
                                 currentPath.includes('/auth/change-password') ||
                                 currentFullUrl.includes('userId=') || // Email link has userId query param
                                 currentFullUrl.includes('code=');     // Email link has code query param
  
  // Define API endpoints that don't require authentication
  const publicApiEndpoints = [
    '/api/auth/restore-pass',
    '/api/auth/reset-pass',
    '/api/auth/change-password',
    '/api/auth/request-password',
    '/api/auth/sign-up',
    '/api/auth/login',
    '/api/auth/VerifyToken',
    '/api/auth/SmsReRegister',
    '/api/auth/VoiceRegister',
    '/api/auth/EmailRegisterGet',
    '/api/auth/EmailRegister'
  ];
  
  const isPublicEndpoint = publicApiEndpoints.some(endpoint => req.url.includes(endpoint));
  
  let clone = req.clone({
    url: url + req.url,
    withCredentials: true,
  });
  
  const storedData = typeof window !== 'undefined' ? localStorage.getItem(authLocalStorageToken) : null;
  
  // Only logout if: no stored data AND not on reset-password page AND not calling public endpoint
  // NOTE: localStorage.getItem returns NULL (not undefined) when key doesn't exist
  if (!storedData && !isOnResetPasswordPage && !isPublicEndpoint) {
    console.log('[AUTH INTERCEPTOR] No token found, not on reset page, not public endpoint - calling logout');
    authService.logout();
    // Return early to prevent request from being made
    return throwError(() => new Error('No authentication token found'));
  }
  
  const parsedData = storedData ? JSON.parse(storedData) : null;
  if (parsedData) {
    clone = addToken(req, parsedData.token);
  } else {
    clone = (req);
  }
  
  return next(clone).pipe(
    catchError((error: HttpErrorResponse) => {
      // Check if error is 401 Unauthorized
      if (error.status === 401) {
        // Don't try to refresh token if on reset-password page or calling public endpoint
        if (isOnResetPasswordPage || isPublicEndpoint) {
          return throwError(() => error);
        }
        
        console.log('[AUTH INTERCEPTOR] 401 Unauthorized - attempting token refresh');
        
        // Try to refresh the token
        return authService.refreshUserToken().pipe(
          switchMap((newTokens) => {
            console.log('[AUTH INTERCEPTOR] Token refresh successful - retrying request');
            // Clone the original request with the new token
            const newReq = addToken(req, newTokens.token);
            // Retry the request with the new token
            return next(newReq);
          }),
          catchError((refreshError) => {
            // If refresh fails, logout user and redirect to login
            console.error('[AUTH INTERCEPTOR] Token refresh failed - logging out and redirecting to login');
            
            // Clear localStorage (browser only)
            if (typeof window !== 'undefined') {
              localStorage.removeItem(authLocalStorageToken);
              
              // Use window.location for immediate redirect (more reliable than router.navigate)
              window.location.href = '/auth/login?sessionExpired=true';
            }
            
            return throwError(() => new Error('Session expired. Please log in again.'));
          })
        );
      }
      // For other errors, just pass through
      return throwError(() => error);
    })
  );
};

function addToken(request: HttpRequest<any>, token: string) {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}
